// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryPageV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Full-page archive of all the user's MIDAS chat threads. Mounted at
// /midas-v2/history under the same MidasRouteGuard chain as /midas-v2.
//
// Layout (per spec §1):
//   • Sticky top bar: "Back to chat" link + "+ New chat" CTA
//   • Title block: "All your chat history" + mono `· N` total + filters
//   • Search input (client-side filter over loaded threads)
//   • Machine chip row (server-side filter)
//   • Date-grouped row list (TODAY / YESTERDAY / THIS WEEK / EARLIER)
//   • "Load more" button — guarded by SEC-REQ-2 offset ceiling
//
// Locked product decisions reflected here:
//   • No `?q=` server search — text filter is client-side over loaded page
//   • No `last_message_preview` field — rows show machine + title + time only
//   • Page size 50 — driven by HISTORY_PAGE_SIZE in the slice
//   • Sort updated_at DESC — server side via existing endpoint
//
// Security:
//   • Inherits MidasRouteGuard from /midas-v2 (no new auth logic)
//   • All thread filtering (userId+customerId scoping) happens server side
//     via JWT — the client never sends customer_id/user_id (SEC-REQ-1)
//   • Offset is capped at HISTORY_OFFSET_CEILING (SEC-REQ-2)
//   • Titles render through React text nodes — no HTML escapes (SEC-REQ-4)
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  setMachineContext,
  startNewChat,
} from "../../../../../shared/chatBot/chatBotSlice";
import { getMachines } from "../../../../machinery/customerMachinerySlice";
import { Machine } from "../../../../machinery/models/models";
import { setActiveThreadId, loadThread } from "../../../midasPageSlice";
import {
  fetchHistoryThreads,
  HistoryMachineFilter,
  HISTORY_OFFSET_CEILING,
  HISTORY_PAGE_SIZE,
  loadMoreHistoryThreads,
  resetHistoryPage,
  setMachineFilter,
  setSearchQuery,
} from "../../../midasHistoryPageSlice";
import { Thread } from "../../../midasPageModels";
import { useMidasBreakpoint } from "../hooks/useMidasBreakpoint";
import MidasHistoryDateGroupV2, {
  DateBucket,
} from "./MidasHistoryDateGroupV2";
import MidasHistoryEmptyV2 from "./MidasHistoryEmptyV2";
import MidasHistoryMachineFiltersV2, {
  MachineChip,
} from "./MidasHistoryMachineFiltersV2";
import MidasHistorySearchV2 from "./MidasHistorySearchV2";
import MidasHistorySkeletonV2 from "./MidasHistorySkeletonV2";

// ════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════

const bucketOf = (iso: string): DateBucket => {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);

  if (d.getTime() >= startOfToday.getTime()) return "today";
  if (d.getTime() >= startOfYesterday.getTime()) return "yesterday";
  if (d.getTime() >= startOfWeek.getTime()) return "thisWeek";
  return "earlier";
};

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

// ════════════════════════════════════════════════════════════════
// MidasHistoryPageV2
// ════════════════════════════════════════════════════════════════

const MidasHistoryPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");
  const { isPhone } = useMidasBreakpoint();

  // Tiny adapter that matches the (key, fallback) shape children use.
  const t = useCallback(
    (key: string, fallback: string) =>
      translate(key as any, { defaultValue: fallback }) as string,
    [translate]
  );

  // ── Slice state ──
  const threads = useAppSelector((s) => s.midasHistoryPage.threads);
  const total = useAppSelector((s) => s.midasHistoryPage.total);
  const offset = useAppSelector((s) => s.midasHistoryPage.offset);
  const hasMore = useAppSelector((s) => s.midasHistoryPage.hasMore);
  const loading = useAppSelector((s) => s.midasHistoryPage.loading);
  const loadingMore = useAppSelector((s) => s.midasHistoryPage.loadingMore);
  const error = useAppSelector((s) => s.midasHistoryPage.error);
  const machineFilter = useAppSelector(
    (s) => s.midasHistoryPage.machineFilter
  );
  const searchQuery = useAppSelector((s) => s.midasHistoryPage.searchQuery);
  const seenMachineIds = useAppSelector(
    (s) => s.midasHistoryPage.seenMachineIds
  );

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  // SEC-REQ-2: Once we hit the ceiling, hide "Load more" even if hasMore is
  // momentarily true. Belt-and-braces guard alongside the slice's check.
  // If `customer_id`/`user_id` params ARE honored by the bot endpoint, file a
  //   P0 bot-side bug — see SEC-REQ-1 verification log in PROGRESS.md.
  const reachedOffsetCeiling =
    offset + HISTORY_PAGE_SIZE > HISTORY_OFFSET_CEILING;
  const showLoadMore = hasMore && !reachedOffsetCeiling;

  // ── Initial load + filter-change refetch ──
  // CRITICAL: This effect must NOT register a cleanup. React fires cleanup on
  // every dependency change, not just unmount — so registering
  // `dispatch(resetHistoryPage())` here turned every chip click into a
  // state-thrash that reset `machineFilter` back to "all" right after the
  // user changed it. That broke the chip filter row entirely (Apr 30 bug:
  // "click Platform → nothing sorts; click All → list empties; click SBL500
  // again → nothing happens"). Unmount-only cleanup lives in the second
  // effect below.
  useEffect(() => {
    dispatch(fetchHistoryThreads({ machineFilter }));
    // Lazy fetch fleet for the chip row
    if (
      flattenMachines(machinesMap).length === 0 &&
      !machineryLoading
    ) {
      dispatch(getMachines({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineFilter]);

  // ── Unmount-only cleanup (separate effect with [] deps so cleanup fires
  //    ONLY on page unmount, NOT on every filter change). ──
  useEffect(() => {
    return () => {
      dispatch(resetHistoryPage());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Machine name lookup for rows ──
  const machineNameById = useMemo(() => {
    const map: Record<string, string> = {};
    const all = flattenMachines(machinesMap);
    all.forEach((m) => {
      const brand = m.customBrandName || m.manufacturer?.name || "";
      const name = brand
        ? `${brand} ${m.model || ""}`.trim()
        : m.model || "";
      if (m.id && name) map[m.id] = name;
    });
    return map;
  }, [machinesMap]);

  // ── Chips: surface every machine the user has chats for. We use the
  //    sticky `seenMachineIds` set (accumulated by the slice across every
  //    successful fetch on this page) instead of recomputing from the
  //    currently-loaded thread set. Without this, switching to "Platform"
  //    refetches threads filtered by `machine_id=null` and the chip row
  //    collapses to nothing — the SBL500 / Mazak / etc. chips disappear.
  //    Sticky set + always re-add the active filter (defensive) means the
  //    chip the user just clicked never vanishes.
  const machineChips: MachineChip[] = useMemo(() => {
    const ids = new Set<string>(seenMachineIds);
    if (
      machineFilter !== "all" &&
      machineFilter !== "platform" &&
      machineFilter
    ) {
      ids.add(machineFilter);
    }
    return Array.from(ids)
      .map((id) => ({
        id,
        label: machineNameById[id] || id.slice(0, 8),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [seenMachineIds, machineNameById, machineFilter]);

  // ── Client-side search filter over loaded threads ──
  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      if (t.title.toLowerCase().includes(q)) return true;
      const mLabel = t.machine_id ? machineNameById[t.machine_id] : null;
      if (mLabel && mLabel.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [threads, searchQuery, machineNameById]);

  // ── Date buckets ──
  const grouped = useMemo(() => {
    const sorted = [...filteredThreads].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at).getTime() -
        new Date(a.last_message_at || a.created_at).getTime()
    );
    const buckets: Record<DateBucket, Thread[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      earlier: [],
    };
    sorted.forEach((t) => {
      buckets[bucketOf(t.last_message_at || t.created_at)].push(t);
    });
    return buckets;
  }, [filteredThreads]);

  // ── Handlers ──
  const handleNewChat = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
    navigate("/midas-v2");
  }, [dispatch, navigate]);

  const handleOpenThread = useCallback(
    (threadId: string) => {
      // Same dispatch pattern the sidebar History tab uses; navigating with the
      // thread already loaded means /midas-v2 renders straight into the right
      // conversation without flashing the welcome state.
      dispatch(loadThread(threadId));
      navigate("/midas-v2");
    },
    [dispatch, navigate]
  );

  const handleMachineFilterChange = useCallback(
    (next: HistoryMachineFilter) => {
      dispatch(setMachineFilter(next));
    },
    [dispatch]
  );

  const handleSearchChange = useCallback(
    (next: string) => {
      dispatch(setSearchQuery(next));
    },
    [dispatch]
  );

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreHistoryThreads());
  }, [dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(fetchHistoryThreads({ machineFilter }));
  }, [dispatch, machineFilter]);

  // ── Body content ──
  let body: React.ReactNode;
  if (loading && threads.length === 0) {
    body = <MidasHistorySkeletonV2 />;
  } else if (error && threads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="error"
        title={t("midas.historyPage.errorTitle", "Couldn't load your history")}
        body={t(
          "midas.historyPage.errorBody",
          "Something went wrong. Try again in a moment."
        )}
        ctaLabel={t("midas.historyPage.errorRetry", "Retry")}
        onCtaClick={handleRetry}
      />
    );
  } else if (filteredThreads.length === 0 && threads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="no-threads"
        title={t("midas.historyPage.emptyTitle", "No conversations yet")}
        body={t(
          "midas.historyPage.emptyBody",
          "Start chatting with MIDAS to build up your history."
        )}
        ctaLabel={t("midas.historyPage.emptyCta", "Start a new chat")}
        onCtaClick={handleNewChat}
      />
    );
  } else if (filteredThreads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="no-results"
        title={t("midas.historyPage.noResultsTitle", "No matches")}
        body={t(
          "midas.historyPage.noResultsBody",
          "Try a different search term or change the machine filter."
        )}
        ctaLabel={
          searchQuery
            ? t("midas.historyPage.noResultsClear", "Clear search")
            : undefined
        }
        onCtaClick={searchQuery ? () => handleSearchChange("") : undefined}
      />
    );
  } else {
    body = (
      // §5.5 audit: gap between date groups bumped 24px → 32px (gap-6 → gap-8)
      // to match the spec's roomy section break.
      <div className="flex flex-col gap-8">
        {(["today", "yesterday", "thisWeek", "earlier"] as DateBucket[]).map(
          (b) => (
            <MidasHistoryDateGroupV2
              key={b}
              bucket={b}
              threads={grouped[b]}
              machineNameById={machineNameById}
              onOpenThread={handleOpenThread}
              t={t}
            />
          )
        )}
        {showLoadMore && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="flex h-11 items-center gap-1.5 rounded-md border border-[rgba(44,38,32,0.12)] bg-white px-4 font-sora text-[13px] font-medium text-midas-graphite outline-none transition-colors duration-150 hover:border-midas-terracotta hover:bg-[#FCFAF4] focus-visible:ring-2 focus-visible:ring-midas-terracotta disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {loadingMore
                ? t("midas.historyPage.loadingMore", "Loading...")
                : t("midas.historyPage.loadMore", "Load more")}
            </button>
          </div>
        )}
        {error && threads.length > 0 && (
          // Non-fatal "load more" failure — surface inline without losing
          // already-loaded rows.
          <p
            role="alert"
            className="text-center font-sora text-[12px] text-midas-terracotta"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {t(
              "midas.historyPage.loadMoreError",
              "Couldn't load more. Try again."
            )}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="midas-v2-root flex h-full min-h-screen w-full flex-col overflow-hidden bg-midas-bone">
      {/* ── Top bar ──
          Spec: minimal centered "HISTORY" eyebrow only.
          §1.1/§1.2 audit: removed the left "Back to chat" link and the right
          "+ New chat" CTA — neither appears in the spec. §1.3: bar height
          dropped from 60px to 44px to match the slim spec proportion. */}
      <header className="flex h-[44px] flex-shrink-0 items-center justify-center border-b border-[rgba(44,38,32,0.08)] bg-midas-bone-light px-3 sm:px-4">
        <span
          className="font-mono text-[11px] uppercase tracking-[0.16em] text-midas-muted-stone"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {t("midas.historyPage.eyebrow", "History")}
        </span>
      </header>

      {/* ── Scroll body ── */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[920px] flex-col gap-6 px-4 py-8 sm:px-6 md:px-8">
          {/* Title block */}
          <div className="flex flex-col gap-2">
            <h1
              // §2.2 audit: tablet heading bumped 32px → 36px (line-height 40 → 44)
              // to match the spec's display-size title.
              className="font-sora text-[28px] font-semibold leading-[34px] tracking-[-0.01em] text-midas-graphite md:text-[36px] md:leading-[44px]"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {t("midas.historyPage.title", "All your chat history")}
            </h1>
            <p
              // §2.5 audit: subtitle bumped 12px → 13px to be readable
              // alongside the heavier heading without competing with it.
              className="font-mono text-[13px] tracking-[0.04em] text-midas-muted-stone"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {`· ${total} ${
                total === 1
                  ? t("midas.historyPage.conversationOne", "conversation")
                  : t("midas.historyPage.conversationMany", "conversations")
              }`}
            </p>
          </div>

          {/* Search */}
          <MidasHistorySearchV2
            value={searchQuery}
            onChange={handleSearchChange}
            showKeyboardHint={!isPhone}
            placeholder={t(
              "midas.historyPage.searchPlaceholder",
              "Search by title or machine"
            )}
            ariaLabel={t(
              "midas.historyPage.searchAriaLabel",
              "Search conversations"
            )}
            clearLabel={t("midas.historyPage.searchClear", "Clear search")}
          />

          {/* Machine filter chips */}
          <MidasHistoryMachineFiltersV2
            active={machineFilter}
            machines={machineChips}
            onChange={handleMachineFilterChange}
            t={t}
          />

          {/* Body
              When a filter re-fetch is in flight AND we still have stale
              rows (because the slice no longer wipes threads on filter
              change — see midasHistoryPageSlice.ts setMachineFilter
              comment), dim the list to signal "refreshing". This replaces
              the old empty→skeleton→rows triple-flash with a single
              opacity transition. `aria-busy` keeps screen readers in sync.
              motion-reduce keeps the dim instant rather than fading. */}
          <div
            aria-busy={loading && threads.length > 0}
            className="transition-opacity duration-150 motion-reduce:transition-none"
            style={{ opacity: loading && threads.length > 0 ? 0.55 : 1 }}
          >
            {body}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MidasHistoryPageV2;
